import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

function getAboutSchema() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'AboutPage',
        name: '关于我们 - 草榴二维码指南',
        description: '草榴二维码指南致力于为草榴社区用户提供最新、最全的入口指南，成立于2020年，已服务数百万用户。',
        url: 'https://caoliu-qrcode.example.com/about',
        isPartOf: { '@id': 'https://caoliu-qrcode.example.com/#website' },
        inLanguage: 'zh-CN',
      },
      {
        '@type': 'Organization',
        name: '草榴二维码指南',
        url: 'https://caoliu-qrcode.example.com',
        description: '致力于为草榴社区用户提供最新、最全的入口指南',
        foundingDate: '2020',
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'contact@caoliu-qrcode.example.com',
          contactType: 'customer service',
          availableLanguage: 'Chinese',
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: 'https://caoliu-qrcode.example.com',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: '关于我们',
          },
        ],
      },
    ],
  };
}

export default function AboutPage() {
  const { t } = useTranslation();
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMsg('');

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setShowSuccess(true);
      form.reset();
      setCharCount(0);
      return;
    }

    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d9q6k3snlsngrm4llse0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // ignore
      }

      if (response.ok && parsed?.code === 'OK') {
        setShowSuccess(true);
        form.reset();
        setCharCount(0);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '发送失败，请稍后重试。';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setErrorMsg('发送失败，请稍后重试。');
        } else {
          setErrorMsg(serverMsg);
        }
      }
    } catch {
      setErrorMsg('网络错误，请检查连接后重试。');
    }
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="关于我们 - 草榴二维码指南"
        description="草榴二维码指南致力于为草榴社区用户提供最新、最全的入口指南，成立于2020年，已服务数百万用户。了解我们的品牌故事与联系方式。"
        keywords="草榴二维码,关于我们,草榴二维码指南,联系我们,草榴社区"
        canonicalUrl="/about"
        schemaData={getAboutSchema()}
      />
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-8">
          <Breadcrumb />

          <div className="mb-12">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
              {t('about.title')}
            </h1>
            <p className="text-sm md:text-base text-foreground-600">
              {t('about.subtitle')}
            </p>
          </div>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              {t('about.intro.title')}
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
              <div className="flex flex-col gap-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                <p>{t('about.intro.paragraph1')}</p>
                <p>{t('about.intro.paragraph2')}</p>
                <p>{t('about.intro.paragraph3')}</p>
              </div>
              <div className="rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20professional%20team%20workspace%20illustration%20with%20warm%20golden%20amber%20tones%2C%20minimalist%20design%2C%20collaborative%20environment%2C%20clean%20geometric%20shapes%2C%20soft%20natural%20lighting%2C%20editorial%20style%2C%20no%20text%2C%20no%20people%20faces%20visible%2C%20warm%20atmosphere&width=700&height=450&seq=about-intro-image&orientation=landscape"
                  alt="草榴二维码指南团队"
                  className="w-full h-full object-cover object-top rounded-lg"
                  title="草榴二维码指南团队"
                />
              </div>
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
              {t('about.reputation.title')}
            </h2>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-10">
              {[
                { value: '200万+', label: t('about.reputation.users') },
                { value: '500+', label: t('about.reputation.updates') },
                { value: '6年', label: t('about.reputation.years') },
                { value: '98%', label: t('about.reputation.satisfaction') },
              ].map((stat) => (
                <div
                  key={stat.label}
                  className="text-center p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/70"
                >
                  <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs md:text-sm text-foreground-600">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            <h3 className="text-lg font-heading font-bold text-foreground-900 mb-6">
              {t('about.reputation.userFeedback')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
              {[
                { text: t('about.reputation.feedback1'), user: t('about.reputation.feedback1User') },
                { text: t('about.reputation.feedback2'), user: t('about.reputation.feedback2User') },
                { text: t('about.reputation.feedback3'), user: t('about.reputation.feedback3User') },
              ].map((fb, i) => (
                <div
                  key={i}
                  className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200/70"
                >
                  <div className="w-8 h-8 flex items-center justify-center mb-3 text-primary-400">
                    <i className="ri-double-quotes-l text-2xl"></i>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    {fb.text}
                  </p>
                  <p className="text-xs font-medium text-primary-600">
                    — {fb.user}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-3">
              {t('about.contact.title')}
            </h2>
            <p className="text-sm text-foreground-600 mb-8">
              {t('about.contact.subtitle')}
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="flex flex-col gap-5">
                {[
                  { icon: 'ri-mail-line', label: t('about.contact.email'), value: 'contact@caoliu-qrcode.example.com', href: 'mailto:contact@caoliu-qrcode.example.com' },
                  { icon: 'ri-wechat-line', label: t('about.contact.wechat'), value: '草榴二维码指南' },
                  { icon: 'ri-qq-line', label: t('about.contact.qq'), value: '888888888' },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-4 p-4 rounded-lg bg-background-100 border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 flex-shrink-0">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-500 mb-0.5">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} className="text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors">
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-sm font-medium text-foreground-800">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-6 rounded-lg bg-background-100 border border-background-200/70">
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-5">
                  {t('about.contact.formTitle')}
                </h3>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="flex flex-col gap-4"
                >
                  <div>
                    <label htmlFor="form-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {t('about.contact.formName')}
                    </label>
                    <input
                      id="form-name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的称呼"
                    />
                  </div>
                  <div>
                    <label htmlFor="form-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {t('about.contact.formEmail')}
                    </label>
                    <input
                      id="form-email"
                      name="email"
                      type="email"
                      required
                      className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入您的电子邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="form-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {t('about.contact.formSubject')}
                    </label>
                    <input
                      id="form-subject"
                      name="subject"
                      type="text"
                      required
                      className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入主题"
                    />
                  </div>
                  <div>
                    <label htmlFor="form-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      {t('about.contact.formMessage')}
                    </label>
                    <textarea
                      id="form-message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full px-3.5 py-2.5 text-sm rounded-md border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容..."
                    />
                    <p className="text-xs text-foreground-400 mt-1">
                      {charCount}/500 {t('about.contact.charLimit')}
                    </p>
                  </div>

                  <div className="form-honeypot-wrapper">
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>

                  <button
                    type="submit"
                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-send-plane-line"></i>
                    </div>
                    {t('about.contact.formSubmit')}
                  </button>

                  {showSuccess && (
                    <div className="p-3 rounded-md bg-accent-100 text-accent-800 text-sm font-medium">
                      <div className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                        <i className="ri-checkbox-circle-line"></i>
                      </div>
                      {t('about.contact.formSuccess')}
                    </div>
                  )}

                  {errorMsg && (
                    <div className="p-3 rounded-md bg-accent-100/70 text-accent-800 text-sm">
                      {errorMsg}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}